// Shared, app-wide date/time formatting (DESIGN_LANGUAGE_SPEC §27 — "shared date
// formatting"). Every list/detail view consumes these helpers so dates render with a
// single, consistent locale and format. Built on the platform `Intl` APIs (no deps).

/** Locale used across the admin SPA. Kept explicit so output is deterministic. */
export const DEFAULT_LOCALE = 'en-US';

export type DateInput = string | number | Date;

export interface FormatDateOptions {
  /** BCP-47 locale override. Defaults to {@link DEFAULT_LOCALE}. */
  locale?: string;
  /** Intl date/time formatting options override. */
  intl?: Intl.DateTimeFormatOptions;
  /** Value rendered when the input cannot be parsed into a valid date. */
  fallback?: string;
}

/** Coerce arbitrary input into a Date, returning `null` for invalid values. */
function toDate(value: DateInput): Date | null {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const DEFAULT_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
};

const DEFAULT_DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

/**
 * Format a date as a short, human-readable string (e.g. `May 30, 2026`).
 * The canonical app-wide date formatter — prefer this over ad-hoc `toLocaleString` calls.
 */
export function formatDate(value: DateInput, opts: FormatDateOptions = {}): string {
  const date = toDate(value);
  if (!date) return opts.fallback ?? '—';
  const locale = opts.locale ?? DEFAULT_LOCALE;
  return new Intl.DateTimeFormat(locale, {
    ...DEFAULT_DATE_OPTIONS,
    ...opts.intl,
  }).format(date);
}

/** Format a date including time (e.g. `May 30, 2026, 09:30 AM`). */
export function formatDateTime(value: DateInput, opts: FormatDateOptions = {}): string {
  const date = toDate(value);
  if (!date) return opts.fallback ?? '—';
  const locale = opts.locale ?? DEFAULT_LOCALE;
  return new Intl.DateTimeFormat(locale, {
    ...DEFAULT_DATE_TIME_OPTIONS,
    ...opts.intl,
  }).format(date);
}

const RELATIVE_DIVISIONS: { amount: number; unit: Intl.RelativeTimeFormatUnit }[] = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  { amount: 4.34524, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
];

/**
 * Format a date relative to `now` (e.g. `3 hours ago`, `in 2 days`).
 * Useful for "last seen" / "created" columns. `now` is injectable for deterministic tests.
 */
export function formatRelative(
  value: DateInput,
  opts: FormatDateOptions & { now?: DateInput } = {},
): string {
  const date = toDate(value);
  if (!date) return opts.fallback ?? '—';
  const now = toDate(opts.now ?? Date.now()) ?? new Date();
  const locale = opts.locale ?? DEFAULT_LOCALE;
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  let duration = (date.getTime() - now.getTime()) / 1000;
  for (const division of RELATIVE_DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return formatter.format(Math.round(duration), division.unit);
    }
    duration /= division.amount;
  }
  return formatter.format(Math.round(duration), 'year');
}

/**
 * Compact relative time for tight UI slots where the long form ("15 seconds ago")
 * wraps to two lines and visually jitters as the value updates each second. Format:
 *
 *   "now"  |  "5s ago"  |  "42m ago"  |  "3h ago"  |  "2d ago"  |  "in 7s" …
 *
 * Keeps the same now-injection seam as `formatRelative` for deterministic tests.
 */
export function formatRelativeCompact(
  value: DateInput,
  opts: { now?: DateInput; fallback?: string } = {},
): string {
  const date = toDate(value);
  if (!date) return opts.fallback ?? '—';
  const now = toDate(opts.now ?? Date.now()) ?? new Date();

  const seconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const abs = Math.abs(seconds);

  // The very-near-now window collapses to a single token instead of "0s ago", which is
  // visually identical to the steady-state "live" badge already pulsing on the page.
  if (abs < 5) return 'now';

  const future = seconds > 0;
  let n: number;
  let unit: string;
  if (abs < 60) {
    n = abs;
    unit = 's';
  } else if (abs < 3600) {
    n = Math.round(abs / 60);
    unit = 'm';
  } else if (abs < 86400) {
    n = Math.round(abs / 3600);
    unit = 'h';
  } else {
    n = Math.round(abs / 86400);
    unit = 'd';
  }

  return future ? `in ${n}${unit}` : `${n}${unit} ago`;
}

// ── Numeric formatting (C29 usage dashboards) ─────────────────────────────────────────────────
// Shared so token counts, request counts, costs and rates render consistently across the SPA.

/** Format an integer with grouping separators (e.g. `1,310,000`). */
export function formatNumber(value: number, locale: string = DEFAULT_LOCALE): string {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(locale).format(value);
}

/**
 * Compact number formatting for headline metrics (e.g. `1.3M`, `845K`). Used where space is tight
 * and exact precision is less important than at-a-glance scale.
 */
export function formatCompact(value: number, locale: string = DEFAULT_LOCALE): string {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(locale, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Format a 0..1 rate as a percentage (e.g. `0.012` → `1.2%`). `digits` controls decimal places.
 */
export function formatPercent(
  rate: number,
  { digits = 1, locale = DEFAULT_LOCALE }: { digits?: number; locale?: string } = {},
): string {
  if (!Number.isFinite(rate)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(rate);
}

/** Format a USD cost estimate (e.g. `42.18` → `$42.18`). Returns `—` for null/NaN. */
export function formatCost(
  value: number | null | undefined,
  locale: string = DEFAULT_LOCALE,
): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}
